import type { EntityState } from '@reduxjs/toolkit'
import { createEntityAdapter } from '@reduxjs/toolkit'

// @ts-ignore
import { CACHE_DURATION_IN_SECONDS } from '@racepointenergy/swc/scs/client/constants'
import { queryFnFactory } from '@racepointenergy/swc/scs/client/dealer-websocket-machine/endpoints/query-fn'
import type { HomeId } from '@racepointenergy/swc/scs/types/uid'

import { api } from '@racepointenergy/swc/client/api'
import { isPlainObject } from '@racepointenergy/swc/client/utils/is-plain-object'

export interface ReadEnergyEventsRequestArguments {
  homeId: HomeId
}

export type ReadEnergyEventsRequestResult = EntityState<Event>

export interface ReadEnergyEventsRequestResponse {
  payload: {
    messages: {
      URI: 'events/energy'
      startIndex: number
      numResults: number
      results: {
        id: number
        eventUnixTime: number
        eventName: string
        eventDetails: {
          loadName: string
        }
        image: string
      }[]
    }[]
  }
}

export interface Event {
  id: number
  eventUnixTime: number
  eventName: string
  eventDetails: {
    loadName: string
  }
  image: string
}

const energyMonitorResultsAdapter = createEntityAdapter<Event>()

const injectedApi = api.injectEndpoints({
  endpoints(builder) {
    return {
      readEnergyEventsRequest: builder.query<
        ReadEnergyEventsRequestResult,
        ReadEnergyEventsRequestArguments
      >({
        keepUnusedDataFor: CACHE_DURATION_IN_SECONDS,
        providesTags(queryResult, queryError, { homeId }) {
          if (queryError) {
            return [{ id: homeId, type: 'scs$homeOnline' }]
          }
          return []
        },
        queryFn({ homeId: id }, queryApi) {
          return queryFnFactory<
            ReadEnergyEventsRequestResult,
            ReadEnergyEventsRequestResponse
          >({
            in: JSON.stringify({
              URI: 'monitor/events/energy',
            }),
            predicate(
              receivedMessage: unknown
            ): receivedMessage is ReadEnergyEventsRequestResponse {
              return (
                isPlainObject(receivedMessage) &&
                receivedMessage?.payload?.URI === 'monitor/events/energy'
              )
            },
            selector({ payload }) {
              return energyMonitorResultsAdapter.setAll(
                energyMonitorResultsAdapter.getInitialState(),
                payload.messages[0].results
              )
            },
          })({ id }, queryApi)
        },
      }),
    }
  },
})

export const readEnergyEventsRequest =
  injectedApi.endpoints.readEnergyEventsRequest
